import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  RowActions,
  SectionCard,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  useOverseasPoliciesQuery,
  useOverseasPolicyMutation,
} from '../hooks/useOverseasShipping';
import { OVERSEAS_SHIPPING_WRITE_ROLES } from '../lib/overseasShippingRbac';
import { OverseasPolicyEditor } from './OverseasPolicyEditor';
import {
  OVERSEAS_STORE_FLAGS,
  type OverseasShippingPolicy,
  type OverseasShippingPolicyPayload,
} from '../types/overseasShipping';

/**
 * 해외 배송비 설정 본문 — 통합 페이지(ShippingManagementPage)의 「해외 배송비」 탭과
 * 단독 페이지(OverseasShippingPage) 양쪽에서 재사용된다. 로직/훅/API 는 무변경이며
 * PageShell 만 외부(컨테이너)가 제공한다. 추가 버튼은 본문 상단 우측 툴바로 렌더한다.
 */
export const OverseasShippingSection = () => {
  const { t } = useTranslation('overseasShipping');
  const canWrite = useHasRole(OVERSEAS_SHIPPING_WRITE_ROLES);
  const query = useOverseasPoliciesQuery();
  const { create, update, remove } = useOverseasPolicyMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OverseasShippingPolicy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OverseasShippingPolicy | null>(null);

  const saving = create.isPending || update.isPending;

  const openCreate = () => {
    setEditTarget(null);
    setEditorOpen(true);
  };
  const openEdit = (policy: OverseasShippingPolicy) => {
    setEditTarget(policy);
    setEditorOpen(true);
  };

  const handleSubmit = (payload: OverseasShippingPolicyPayload) => {
    if (editTarget) {
      update.mutate(
        { policyId: editTarget.policyId, payload },
        {
          onSuccess: () => {
            toast.success(t('overseasShipping.toast.updated'));
            setEditorOpen(false);
          },
          onError: (err) => toast.error(friendlyMessage(err, t('overseasShipping.toast.updateError'))),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success(t('overseasShipping.toast.created'));
          setEditorOpen(false);
        },
        onError: (err) => toast.error(friendlyMessage(err, t('overseasShipping.toast.createError'))),
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.policyId, {
      onSuccess: () => {
        toast.success(t('overseasShipping.toast.deleted'));
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(friendlyMessage(err, t('overseasShipping.toast.deleteError'))),
    });
  };

  const columns: DataTableColumn<OverseasShippingPolicy>[] = [
    {
      key: 'store',
      header: t('overseasShipping.columns.store'),
      width: 120,
      cell: (row) => (
        <span className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--content-strong)' }}>
          <span aria-hidden="true">{OVERSEAS_STORE_FLAGS[row.storeScope]}</span>
          {t(`overseasShipping.storeScope.${row.storeScope}`)}
        </span>
      ),
    },
    {
      key: 'basis',
      header: (
        <span title={t('overseasShipping.columns.basisTooltip')}>
          {t('overseasShipping.columns.basis')}
        </span>
      ),
      width: 110,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t(`overseasShipping.basis.${row.basis}`)}
        </span>
      ),
    },
    {
      key: 'insurance',
      header: t('overseasShipping.columns.insurance'),
      width: 90,
      align: 'center',
      cell: (row) =>
        row.insuranceRequired ? (
          <StatusBadge label={t('overseasShipping.badge.insuranceRequired')} variant="info" size="sm" />
        ) : (
          <StatusBadge label={t('overseasShipping.badge.insuranceOptional')} variant="neutral" size="sm" />
        ),
    },
    {
      key: 'boxWeight',
      header: (
        <span title={t('overseasShipping.columns.boxWeightTooltip')}>
          {t('overseasShipping.columns.boxWeight')}
        </span>
      ),
      width: 100,
      align: 'right',
      cell: (row) => (
        <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.boxWeightKg} kg
        </span>
      ),
    },
    {
      key: 'groups',
      header: (
        <span title={t('overseasShipping.columns.groupsTooltip')}>
          {t('overseasShipping.columns.groups')}
        </span>
      ),
      width: 80,
      align: 'right',
      cell: (row) => (
        <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.countryGroups.length}
        </span>
      ),
    },
    {
      key: 'active',
      header: t('overseasShipping.columns.status'),
      width: 90,
      align: 'center',
      cell: (row) => (
        <StatusBadge
          label={row.active ? t('overseasShipping.badge.active') : t('overseasShipping.badge.inactive')}
          variant={row.active ? 'success' : 'neutral'}
          size="sm"
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      align: 'right',
      cell: (row) => {
        if (!canWrite) return null;
        const actions: RowActionItem[] = [
          { key: 'edit', label: t('overseasShipping.rowAction.edit'), onClick: () => openEdit(row) },
          {
            key: 'delete',
            label: t('overseasShipping.rowAction.delete'),
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel={t('overseasShipping.rowAction.trigger')} actions={actions} />;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="overseas-shipping-section">
      {canWrite ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={openCreate} data-testid="overseas-add">
            <Plus size={15} aria-hidden="true" />
            {t('overseasShipping.section.add')}
          </Button>
        </div>
      ) : null}

      <SectionCard title={t('overseasShipping.section.listTitle')}>
        {query.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[var(--density-compact-row-h)] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>{t('overseasShipping.section.loadError')}</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              {t('overseasShipping.section.retry')}
            </Button>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={query.data ?? []}
            rowKey={(row) => row.policyId}
            emptyState={<EmptyState size="inline" title={t('overseasShipping.section.empty')} />}
            stickyHeader
            ariaLabel={t('overseasShipping.section.ariaList')}
          />
        )}
      </SectionCard>

      <OverseasPolicyEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        target={editTarget}
        saving={saving}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('overseasShipping.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('overseasShipping.deleteDialog.body', {
                store: deleteTarget ? t(`overseasShipping.storeScope.${deleteTarget.storeScope}`) : '',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('overseasShipping.deleteDialog.cancel')}
              </Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={remove.isPending} onClick={handleDelete}>
              {t('overseasShipping.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
